import React from 'react'
import PropTypes from 'prop-types'
import { graphql, navigate, prefetchPathname } from 'gatsby'
import ThemeProvider from 'react-css-themr/lib/components/ThemeProvider'
// import PageTransition from "../components/page-transition"
import get from 'lodash/get'
import dot from 'dot-object'
import Mapper from '../components/mapper'
import Badge from '../components/badge'
import CookieMapper from '../components/cookies'
import { KEYBOARD_CLOSED, KEYBOARD_OPENED } from '../globals/events'
import { setGlobals } from '../globals/globals'
import {
  trackAnswerClick,
  trackButtonClick,
  trackCalendly,
  trackFormSubmit,
  trackMultipleChoiceClick,
  trackPage,
} from '../globals/tracking/track-handler'
import {
  getCampaignCookieAccepted,
  setCampaignCookieAccepted,
} from '../globals/storage'
import PageContent from '../components/page-content'
import linking from '../helper/linking'
import getButton from '../helper/get-button'

export default class PageTemplate extends React.Component {
  constructor(props) {
    super(props)

    this.state = { cookiesAccepted: false }

    this.setHandlers()
    this.setInputMap()
    this.setImageMap()
    this.setMapperProps()
    this.setThirdPartyMap()

    setGlobals({
      perspectivePageId: this.props.pageContext.page._id,
      perspectiveCompanyId: this.props.pageContext.page.companyId,
      perspectiveCampaignId: this.props.data.campaignData._id,
      perspectiveVersionId: this.props.data.campaignData.campaignVersionId,
    })
  }

  componentDidMount() {
    if (this.props.pageContext.nextPageId && this.props.pageContext.nextPageId !== 'result') {
      setTimeout(() => {
        prefetchPathname(this.props.pageContext.nextPageId)
      }, 500)
    }

    this.trackCurrentPage()
    this.setEvents()
  }

  extendResultMapping(answerId) {
    const completeMapping = get(this.props.data.resultMapping, 'mapping', [])
    const componentMapping = completeMapping.find(({ source }) => source === answerId)

    if (componentMapping) {
      return {
        [this.props.pageContext.page._id]: componentMapping,
      }
    }

    return {}
  }

  setHandlers() {
    this.handlers = {
      formSubmit: this.handleForm,
      thirdPartyInteraction: this.handleThirdPartyInteraction,
      button: this.handleButton,
      answer: this.handleAnswer,
      multipleChoice: this.handleMultipleChoice,
      goToFirstPage: this.navigateToFirstPage,
      handleKeyboardOpened: this.handleKeyboardOpened,
      handleKeyboardClosed: this.handleKeyboardClosed,
    }
  }

  setMapperProps() {
    this.mapperProps = {
      handlers: this.handlers,
      inputMap: this.inputMap,
      imageMap: this.imageMap,
      nextPageId: this.props.pageContext.nextPageId,
      campaignId: this.props.data.campaignData._id,
    }
  }

  setThirdPartyMap() {
    this.thirdPartyMap = {
      calendly: (formComponent) => {
        this.trackCalendly()

        this.formGoNext(formComponent, false)
      },
    }
  }

  setEvents() {
    try {
      this.KeyboardOpenedEvent = document.createEvent('Event')
      this.KeyboardOpenedEvent.initEvent(KEYBOARD_OPENED, true, true)

      this.KeyboardClosedEvent = document.createEvent('Event')
      this.KeyboardClosedEvent.initEvent(KEYBOARD_CLOSED, true, true)
    } catch (err) {
      this.KeyboardOpenedEvent = undefined
      this.KeyboardClosedEvent = undefined
    }
  }

  setInputMap() {
    this.inputMap = JSON.parse(this.props.data.inputMap.internal.content).inputMap
  }

  setImageMap() {
    this.imageMap = JSON.parse(this.props.data.imageMap.internal.content)
  }

  trackCurrentPage() {
    trackPage(this.props.pageContext.page, this.props.pageContext.isLastPage)
  }

  handleKeyboardOpened = () => {
    if (this.KeyboardOpenedEvent) {
      window.dispatchEvent(this.KeyboardOpenedEvent)
    }
  }

  handleKeyboardClosed = () => {
    if (this.KeyboardClosedEvent) {
      window.dispatchEvent(this.KeyboardClosedEvent)
    }
  }

  handleForm = (rawValues, formComponent, skipNavigation) => {
    if (skipNavigation) {
      this.trackForm(rawValues, formComponent)

      const buttonComponent = getButton(formComponent)
      return trackButtonClick(buttonComponent._id)
    }
    return this.trackFormAndGoNext(rawValues, formComponent)
  }

  handleThirdPartyInteraction = (thirdParty, component) => {
    if (this.thirdPartyMap[thirdParty]) {
      this.thirdPartyMap[thirdParty](component)
    }
  }

  trackFormAndGoNext = (rawValues, formComponent) => {
    this.trackForm(rawValues, formComponent)
    this.formGoNext(formComponent)
  }

  formGoNext = (formComponent, trackButton = true) => {
    const buttonComponent = getButton(formComponent)

    if (trackButton) {
      return this.trackButtonAndGoNext(buttonComponent)
    }

    return this.goNextWithButton(buttonComponent)
  }

  trackCalendly = () => {
    trackCalendly()
  }

  trackForm = (rawValues, formComponent) => {
    trackFormSubmit({
      formId: formComponent._id,
      formValues: dot.dot(rawValues), // compatible with nested paths
      fields: this.props.data.fieldForm.dataFields,
      inputMap: this.inputMap,
    })

    const campaignId = this.props.data.campaignData._id

    const cookie = getCampaignCookieAccepted(campaignId)
    if (!cookie) {
      setCampaignCookieAccepted(campaignId)
    }
  }

  handleButton = (val, extraState = {}, component) => {
    if (component.content.buttonType === 'submit') {
      return
    }

    this.trackButtonAndGoNext(component)
  }

  trackButtonAndGoNext(component) {
    trackButtonClick(component._id)

    this.goNextWithButton(component)
  }

  goNextWithButton(button) {
    if (button.content.linking.isInternal) {
      this.navigateToNextPage(button.content.linking.pageId)
    } else {
      window.delay(linking(button))
    }
  }

  handleAnswer = (component) => {
    trackAnswerClick({
      answer: component,
      fields: this.props.data.fieldQuestion.dataFields,
    })

    if (component.content.linking.isInternal) {
      this.navigateToNextPage(component.content.linking.pageId, {
        resultMapping: {
          ...get(this.props.location.state, 'resultMapping', {}),
          ...this.extendResultMapping(component._id),
        },
      })
    } else {
      window.delay(linking(component))
    }
  }

  handleMultipleChoice = (multipleChoiceComponent, answerIds) => {
    trackMultipleChoiceClick({
      answerIds,
      parentComponent: multipleChoiceComponent,
      fields: this.props.data.fieldQuestion.dataFields,
    })

    const buttonComponent = multipleChoiceComponent.components[multipleChoiceComponent.components.length - 1]

    this.trackButtonAndGoNext(buttonComponent)
  }

  navigateToNextPage = (nextPageId = this.props.pageContext.nextPageId, state = {}) => {
    const nextState = {
      ...this.props.location.state,
      ...state,
    }
    if (nextPageId) {
      const nextPath = this.injectDynamicResultPage(nextPageId, nextState)
      navigate(`/${nextPath}`, {
        state: nextState,
      })
    }
  }

  injectDynamicResultPage(nextPageId, state) {
    if (nextPageId !== 'result') {
      return nextPageId
    }

    return this.calculateResultPage(state)
  }

  calculateResultPage(state = {}) {
    const counts = {}

    Object.values(get(state, 'resultMapping', {})).forEach((map) => {
      if (!counts[map.destination]) {
        counts[map.destination] = {
          destination: map.destination,
          value: 0,
        }
      }
      counts[map.destination].value += map.value
    })

    const max = Object.values(counts).sort((prev, next) => {
      return next.value - prev.value
    })[0]

    if (!max) {
      return this.getFirstResultPageInList()
    }

    const foundPage = this.props.data.campaignData.pages.find(({ _id }) => {
      return _id === max.destination
    })

    // prevent the user from seeing 404s
    if (!foundPage) {
      return this.getFirstResultPageInList()
    }

    return max.destination
  }

  getFirstResultPageInList() {
    return this.props.data.campaignData.pages.filter(({ isResult }) => isResult).map(({ _id }) => _id)[0]
  }

  navigateToFirstPage = () => {
    window.location.href = window.location.href.replace(this.props.pageContext.pageId, '')
  }

  handleCookieAccept = () => {
    // just to trigger rerender of the page
    this.setState({ cookiesAccepted: true })
  }

  render() {
    const { pageContext } = this.props

    return (
      <React.Fragment>
        <ThemeProvider>
          <React.Fragment>
            <Mapper
              {...this.mapperProps}
              key={pageContext.header._id}
              component={pageContext.header}
            />

            <PageContent
              page={pageContext.page}
              mapperProps={this.mapperProps}
              footer={pageContext.footer}
              showBadge={pageContext.showBadge}
            />
          </React.Fragment>
        </ThemeProvider>
        <Badge key="badge"/>
        <CookieMapper key="cookie" onAcceptClick={this.handleCookieAccept}/>
      </React.Fragment>
    )
  }
}

PageTemplate.propTypes = {
  data: PropTypes.object({
    campaignData: PropTypes.object({
      _id: PropTypes.string().isRequired,
      cookieOverlay: PropTypes.object({
        layout: PropTypes.oneOf(['none', 'normal', 'explicit']).isRequired,
        text: PropTypes.string(),
        linkTitle: PropTypes.string(),
        linkUrl: PropTypes.string(),
        acceptButtonText: PropTypes.string(),
        declineButtonText: PropTypes.string(),
      }),
    }).isRequired,

    inputMap: PropTypes.object({
      internal: PropTypes.object({
        internal: PropTypes.string().isRequired,
      }).isRequired,
    }).isRequired,

    fieldQuestion: PropTypes.object({
      dataFields: PropTypes.object(),
    }),
  }).isRequired,

  pageContext: PropTypes.object({
    page: PropTypes.object({
      _id: PropTypes.string().isRequired,
      name: PropTypes.string().isRequired,
      components: PropTypes.array(),
    }).isRequired,
    nextPageId: PropTypes.string().isRequired,
    isLastPage: PropTypes.string().isRequired,
    pageId: PropTypes.string().isRequired,
    header: PropTypes.object({
      _id: PropTypes.string().isRequired,
    }).isRequired,
    footer: PropTypes.object({
      _id: PropTypes.string().isRequired,
    }).isRequired,
    firstPageId: PropTypes.string().isRequired,
    url: PropTypes.string().isRequired,
    sortedPages: PropTypes.array(),
  }).isRequired,
}

export const query = graphql`
    query GetInputMap($pageId: String) {
        inputMap(pageId: { eq: $pageId }) {
            internal {
                content
            }
        }
        imageMap {
            internal {
                content
            }
        }
        campaignData {
            _id
            cookieOverlay {
                layout
            }
            pages {
                _id
                isResult
            }
            campaignVersionId
        }
        fieldForm(pageId: { eq: $pageId }) {
            dataFields {
                _id
                title
                interactionType
                pageId
                componentId
                additional {
                    form {
                        componentId
                        inputs {
                            _id
                            componentId
                            fieldName
                            inputType
                            required
                            visual
                            conversionTrigger
                        }
                    }
                }
            }
        }
        fieldQuestion(pageId: { eq: $pageId }) {
            dataFields {
                _id
                title
                interactionType
                pageId
                componentId
                additional {
                    answers {
                        _id
                        componentId
                        fieldName
                        answerTextId
                        value
                        answerMediaId
                    }
                }
            }
        }
        resultMapping(pageId: { eq: $pageId }) {
            mapping {
                value
                destination
                source
            }
        }
    }
`
